// Inline toast primitive — Polish B2.
//
// Tiny single-stack toast container. No external library; ~50
// lines of state + a hook that pushes a message + auto-dismisses.
// Fires only on saved-filter Save / Delete success or transition
// success today; promote to a shared design-system surface once
// another feature wants the same affordance.

import { useEffect } from "react";


export interface ToastMessage {
  id: string;
  text: string;
  /** Optional kind — drives the small accent stripe. */
  kind?: "success" | "error" | "info";
}


interface ToastContainerProps {
  toasts: ToastMessage[];
  onDismiss: (id: string) => void;
}


export function ToastContainer({
  toasts, onDismiss,
}: ToastContainerProps) {
  return (
    <div
      data-testid="toast-container"
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2"
    >
      {toasts.map((t) => (
        <ToastItem
          key={t.id}
          toast={t}
          onDismiss={() => onDismiss(t.id)}
        />
      ))}
    </div>
  );
}


function ToastItem({
  toast, onDismiss,
}: { toast: ToastMessage; onDismiss: () => void }) {
  useEffect(() => {
    const timer = window.setTimeout(onDismiss, 3500);
    return () => window.clearTimeout(timer);
  }, [onDismiss]);
  const tint = toast.kind === "error"
    ? "border-ember/40 bg-ember-tint text-ember-ink"
    : toast.kind === "info"
      ? "border-border-subtle bg-raised text-text-primary"
      : "border-sage/40 bg-sage-tint text-teal";
  return (
    <div
      data-testid={`toast-${toast.id}`}
      className={[
        "pointer-events-auto rounded-md border px-3 py-2 text-sm shadow-lg",
        tint,
      ].join(" ")}
    >
      {toast.text}
    </div>
  );
}
